import { useEffect, useState } from 'react'
import { Card } from '@/components/Card'
import { StatusBadge } from '@/components/StatusBadge'
import { fetchApi } from '@/lib/api'
import { RefreshCw, ExternalLink } from 'lucide-react'
import { formatDateTime } from '@/lib/date'

type WorkflowRun = {
  id: number
  name: string
  status: string
  conclusion: string
  branch: string
  commit_sha: string
  commit_message: string
  created_at: string
  updated_at: string
  html_url: string
}

const REPOS = ['twp-front', 'twp-backend', 'twp-cdk', 'twp-orchestrator', 'twp-mcp']

export function CICD() {
  const [selectedRepo, setSelectedRepo] = useState(REPOS[0])
  const [runs, setRuns] = useState<WorkflowRun[]>([])
  const [loading, setLoading] = useState(true)

  const load = async () => {
    setLoading(true)
    try {
      const data = await fetchApi<{ runs: WorkflowRun[] }>(`/cicd/workflows/${selectedRepo}`)
      setRuns(data.runs)
    } catch {
      setRuns([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [selectedRepo])

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold">CI/CD Pipelines</h2>
        <button
          onClick={load}
          disabled={loading}
          className="flex items-center gap-1 px-3 py-1 text-sm rounded bg-[var(--color-bg-hover)] hover:bg-[var(--color-border)] transition-colors"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Repo tabs */}
      <div className="flex gap-1 border-b border-[var(--color-border)]">
        {REPOS.map((repo) => (
          <button
            key={repo}
            onClick={() => setSelectedRepo(repo)}
            className={`px-3 py-2 text-sm border-b-2 transition-colors ${
              selectedRepo === repo
                ? 'border-[var(--color-primary)] text-[var(--color-primary)]'
                : 'border-transparent text-[var(--color-text-muted)] hover:text-[var(--color-text)]'
            }`}
          >
            {repo}
          </button>
        ))}
      </div>

      {/* Workflow runs */}
      <Card>
        {loading ? (
          <p className="text-sm text-[var(--color-text-muted)]">Loading...</p>
        ) : (
          <div className="space-y-2">
            {runs.map((run) => (
              <div
                key={run.id}
                className="flex items-center justify-between p-3 rounded bg-[var(--color-bg)]"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <StatusBadge status={run.conclusion ?? run.status} />
                    <span className="text-sm font-medium truncate">{run.name}</span>
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-xs text-[var(--color-text-muted)]">
                    <span className="font-mono">{run.branch}</span>
                    <span className="font-mono">{run.commit_sha}</span>
                    <span className="truncate max-w-64">{run.commit_message}</span>
                  </div>
                </div>
                <div className="flex items-center gap-3 ml-4">
                  <span className="text-xs text-[var(--color-text-muted)]">
                    {formatDateTime(run.updated_at)}
                  </span>
                  <a
                    href={run.html_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
                  >
                    <ExternalLink size={14} />
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  )
}
